import { IResource } from "@gettactic/api";
import { useAuthenticated, useSettings } from "@gettactic/hooks";
import React from "react";
import { useMapBuilderMappedResourcesData } from "./hooks/useMapBuilderMappedResourcesData";
import { MapView } from "./MapView";
import {
  LegendBridgeData,
  MapCurrentPosition,
  MapInteractiveZoneActionOptions,
  MapInteractiveZones,
  ViewSVGLegendHandle,
  ZoneLink
} from "./types";

export interface NewMapViewProps {
  floor?: IResource;
  resources: IResource[];
  interactiveZoneInfo?: MapInteractiveZones;
  highlightResource?: (
    resId: string | null,
    options: MapInteractiveZoneActionOptions
  ) => void;
  /** Handler for zones linked to another office map / external URL. */
  onZoneLinkClick?: (link: ZoneLink) => void;
  mapEditPosition?: MapCurrentPosition | null;
  miniMapWidth?: number;
  miniMapHeight?: number;
  useSvg?: boolean;
  style?: React.SVGProps<SVGSVGElement>["style"];
  children?: React.ReactNode;
  isDark?: boolean;
  /** Suppress the zone-type legend overlay (e.g., while the map editor
   *  is open so the legend doesn't sit above the editor UI). */
  hideZoneLegend?: boolean;
  /** Forwarded to ZoneLegend — see its `bottomOffset` doc. */
  legendBottomOffset?: number | string;
  /** Forwarded to ViewSVG — see its own doc. */
  bridgeZoneLegend?: boolean;
  onLegendDataChange?: (data: LegendBridgeData) => void;
  legendControlRef?: React.RefObject<ViewSVGLegendHandle | null>;
  /** Per-resource fill override (heatmaps): resource id -> CSS colour. */
  resourceColors?: Record<string, string>;
  /** Per-zone fill override (campus heatmaps): zone id -> CSS colour. */
  zoneColors?: Record<string, string>;
  /** Crop the SVG rendering to one region of the map (Settings previews). */
  focusBounds?: { x: number; y: number; width: number; height: number };
}

export const MapBuilderViewer = React.forwardRef<
  SVGSVGElement | null,
  NewMapViewProps
>(
  (
    {
      floor,
      resources,
      interactiveZoneInfo,
      highlightResource,
      onZoneLinkClick,
      mapEditPosition,
      miniMapWidth,
      miniMapHeight,
      useSvg = false,
      style,
      children,
      isDark,
      hideZoneLegend,
      legendBottomOffset,
      bridgeZoneLegend,
      onLegendDataChange,
      legendControlRef,
      resourceColors,
      zoneColors,
      focusBounds
    },
    ref
  ) => {
    const floorData = useMapBuilderMappedResourcesData(floor, resources);

    const {
      userContext: { authenticatedUser, currentOffice }
    } = useAuthenticated();
    const { isOfficePrivacyEnabled } = useSettings();

    const isPrivacyEnabled = isOfficePrivacyEnabled(currentOffice?.id);

    if (!floor) return null;

    return (
      <MapView
        value={floorData}
        resourceColors={resourceColors}
        zoneColors={zoneColors}
        initialPosition={mapEditPosition || undefined}
        interactiveZones={interactiveZoneInfo}
        highlightResource={highlightResource}
        onZoneLinkClick={onZoneLinkClick}
        key={floor?.id}
        authenticatedUser={authenticatedUser}
        isPrivacyMode={isPrivacyEnabled}
        miniMapWidth={miniMapWidth}
        miniMapHeight={miniMapHeight}
        useSVG={useSvg}
        ref={ref}
        style={style}
        isDark={isDark}
        hideZoneLegend={hideZoneLegend}
        legendBottomOffset={legendBottomOffset}
        bridgeZoneLegend={bridgeZoneLegend}
        onLegendDataChange={onLegendDataChange}
        legendControlRef={legendControlRef}
        focusBounds={focusBounds}
      >
        {children}
      </MapView>
    );
  }
);
